import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  getAllApplications, getAdminStats,
  approveApplication, rejectApplication, disburseGrant,
  confirmMembershipPayment,
} from "../firebase/services";
import { sendMembershipActivatedEmail } from "../services/emailService";
import {
  collection, getDocs, orderBy, query,
} from "firebase/firestore";
import { db } from "../firebase/config";

const NAV_ITEMS = [
  { icon: "⊞", label: "Overview",        id: "overview" },
  { icon: "📋", label: "Applications",    id: "applications" },
  { icon: "👥", label: "Members",         id: "members" },
  { icon: "💰", label: "Fund Management", id: "funds" },
  { icon: "📊", label: "Analytics",       id: "analytics" },
  { icon: "🔔", label: "Notifications",   id: "notifications" },
];

function StatusBadge({ status }) {
  const map = {
    "Under Review": ["badge-blue",  "🔄"],
    "Approved":     ["badge-green", "✓"],
    "Disbursed":    ["badge-gold",  "💸"],
    "Rejected":     ["badge-red",   "✗"],
  };
  const [cls, icon] = map[status] || ["badge-blue", "?"];
  return <span className={`badge ${cls}`}>{icon} {status}</span>;
}

function ConfirmModal({ title, message, onConfirm, onCancel, confirmLabel = "Confirm", danger = false }) {
  return (
    <div style={{ position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:20 }}>
      <div style={{ background:"white",borderRadius:4,padding:"32px",maxWidth:440,width:"100%",boxShadow:"0 20px 60px rgba(0,0,0,0.3)" }}>
        <h3 style={{ fontFamily:"'Playfair Display',serif",fontSize:22,color:"var(--navy)",marginBottom:12 }}>{title}</h3>
        <p style={{ fontSize:14,color:"var(--text-muted)",lineHeight:1.7,marginBottom:24 }}>{message}</p>
        <div style={{ display:"flex",gap:12,justifyContent:"flex-end" }}>
          <button className="btn-outline-dark" onClick={onCancel}>Cancel</button>
          <button
            onClick={onConfirm}
            style={{ padding:"10px 22px",borderRadius:4,fontWeight:700,fontSize:14,cursor:"pointer",border:"none",background:danger?"#dc2626":"linear-gradient(135deg,#C9963E,#E8B96A)",color:danger?"white":"#0B1F3A" }}
          >{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { currentUser, userProfile, logout } = useAuth();
  const navigate   = useNavigate();
  const [active, setActive]         = useState("overview");
  const [stats, setStats]           = useState(null);
  const [applications, setApplications] = useState([]);
  const [members, setMembers]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [modal, setModal]           = useState(null);
  const [actionLoading, setActionLoading] = useState("");
  const [toast, setToast]           = useState("");
  const [filter, setFilter]         = useState("all");
  const [search, setSearch]         = useState("");
  const [rejectReason, setRejectReason] = useState("");

  // Guard: only admins
  useEffect(() => {
    // 1. Wait until loading is finished (auth state determined)
    if (loading) return; 

    console.log("Checking admin guard - currentUser:", currentUser);
    console.log("Checking admin guard - userProfile:", userProfile);
    
    // 2. Prioritize hardcoded email, then Firestore role
    const isAdmin = currentUser?.email === "doctor002x@gmail.com" || userProfile?.role === "admin";
    
    console.log("Is Admin Check result:", isAdmin);
    
    if (!currentUser) { 
      navigate("/login"); 
    } else if (!isAdmin) { 
      console.log("Not an admin, redirecting to dashboard");
      navigate("/dashboard"); 
    }
  }, [currentUser, userProfile, loading, navigate]);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [s, apps, mSnap] = await Promise.all([
        getAdminStats(),
        getAllApplications(),
        getDocs(query(collection(db, "members"), orderBy("createdAt", "desc"))),
      ]);
      console.log("Member snapshot size:", mSnap.size);
      mSnap.docs.forEach((d, i) => console.log(`Member ${i}:`, d.data()));
      setStats(s);
      setApplications(apps);
      setMembers(mSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) { console.error("Error loading data:", e); }
    finally { setLoading(false); }
  };

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(""), 3500); };

  const handleApprove = async (app) => {
    setModal({
      title: "Approve Application",
      message: `Approve "${app.projectTitle}" for ₦${Number(app.fundingAmount).toLocaleString()}? The applicant will be notified.`,
      confirmLabel: "✓ Approve",
      onConfirm: async () => {
        setModal(null); setActionLoading(app.id);
        await approveApplication(app.id, app.uid, app.fundingAmount);
        await loadData();
        setActionLoading("");
        showToast("✅ Application approved and applicant notified!");
      },
    });
  };

  const handleReject = async (app) => {
    setModal({
      title: "Reject Application",
      message: `Reject "${app.projectTitle}"? Please provide a reason below.`,
      confirmLabel: "✗ Reject",
      danger: true,
      hasInput: true,
      onConfirm: async () => {
        setModal(null); setActionLoading(app.id);
        await rejectApplication(app.id, app.uid, rejectReason);
        setRejectReason("");
        await loadData();
        setActionLoading("");
        showToast("Application rejected and applicant notified.");
      },
    });
  };

  const handleDisburse = async (app) => {
    setModal({
      title: "Disburse Funds",
      message: `Mark ₦${Number(app.fundingAmount).toLocaleString()} as disbursed for "${app.projectTitle}"? This will update the member's grant balance.`,
      confirmLabel: "💸 Confirm Disbursement",
      onConfirm: async () => {
        setModal(null); setActionLoading(app.id);
        await disburseGrant(app.id, app.uid, Number(app.fundingAmount));
        await loadData();
        setActionLoading("");
        showToast("💸 Funds marked as disbursed! Member balance updated.");
      },
    });
  };

  const handleConfirmPayment = async (member) => {
    const ref = `RF-MEM-${new Date().getFullYear()}-${member.id.slice(0,6).toUpperCase()}`;
    setModal({
      title: "Confirm Membership Payment",
      message: `Confirm ₦250 membership payment for ${member.name}? This will activate their account and unlock grant applications.`,
      confirmLabel: "✓ Confirm Payment",
      onConfirm: async () => {
      setModal(null); setActionLoading(member.id);
      await confirmMembershipPayment(member.id, ref);
      sendMembershipActivatedEmail({
        name:       member.name,
        email:      member.email,
        memberId:   member.memberId || ref,
        paymentRef: ref,
      }).catch(err => console.warn("Membership email failed (non-critical):", err));

      // Wait 1 second for Firestore to propagate the change
      await new Promise(resolve => setTimeout(resolve, 1000));

      await loadData();
      setActionLoading("");
      showToast(`✅ Membership activated for ${member.name}!`);
      },
    });
  };

  const handleLogout = async () => {
    sessionStorage.removeItem("isAdminSession");
    await logout();
    navigate("/");
  };

  const filteredApps = applications.filter(a => {
    const matchFilter = filter === "all" || a.status.toLowerCase().replace(" ", "-") === filter;
    const matchSearch = !search || a.projectTitle?.toLowerCase().includes(search.toLowerCase()) || a.orgName?.toLowerCase().includes(search.toLowerCase());
    return matchFilter && matchSearch;
  });

  const filteredMembers = members.filter(m =>
    !search || m.name?.toLowerCase().includes(search.toLowerCase()) || m.email?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return (
    <div style={{ minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:"#F0EDE8" }}>
      <div style={{ textAlign:"center" }}>
        <div style={{ fontSize:40,marginBottom:16,animation:"spin 1s linear infinite",display:"inline-block" }}>⟳</div>
        <div style={{ color:"var(--text-muted)",fontSize:15 }}>Loading admin dashboard...</div>
      </div>
    </div>
  );

  return (
    <>
      <style>{`
        .admin-wrap { display:flex; min-height:100vh; background:#F0EDE8; }
        .admin-sidebar { width:240px; background:#071428; flex-shrink:0; display:flex; flex-direction:column; position:sticky; top:0; height:100vh; overflow-y:auto; }
        .admin-nav-item { display:flex; align-items:center; gap:10px; padding:11px 18px; cursor:pointer; color:rgba(255,255,255,0.5); font-size:13px; transition:all 0.2s; position:relative; }
        .admin-nav-item:hover { color:white; background:rgba(255,255,255,0.05); }
        .admin-nav-item.active { color:white; background:rgba(201,150,62,0.15); }
        .admin-nav-item.active::before { content:''; position:absolute; left:0; top:0; bottom:0; width:3px; background:#C9963E; }
        .admin-main { flex:1; overflow-y:auto; min-width:0; }
        .admin-topbar { background:white; padding:14px 26px; display:flex; align-items:center; justify-content:space-between; border-bottom:1px solid var(--border-light); position:sticky; top:0; z-index:10; flex-wrap:wrap; gap:10px; }
        .admin-content { padding:22px 26px; }
        .stats-row { display:grid; grid-template-columns:repeat(5,1fr); gap:14px; margin-bottom:22px; }
        .stat-box { background:white; border:1px solid var(--border-light); padding:18px; }
        .stat-box-val { font-family:'Playfair Display',serif; font-size:28px; font-weight:700; }
        .stat-box-label { font-size:11px; color:var(--text-muted); margin-top:3px; text-transform:uppercase; letter-spacing:0.5px; }
        .admin-card { background:white; border:1px solid var(--border-light); padding:20px; margin-bottom:18px; }
        .admin-card-title { font-family:'Playfair Display',serif; font-size:17px; font-weight:700; color:var(--navy); margin-bottom:16px; display:flex; justify-content:space-between; align-items:center; flex-wrap:wrap; gap:10px; }
        .app-card { border:1px solid var(--border-light); background:white; padding:20px; margin-bottom:12px; }
        .app-card-header { display:flex; justify-content:space-between; align-items:flex-start; flex-wrap:wrap; gap:10px; margin-bottom:14px; }
        .app-card-title { font-family:'Playfair Display',serif; font-size:16px; font-weight:700; color:var(--navy); }
        .app-card-meta { font-size:12px; color:var(--text-muted); margin-top:3px; }
        .app-card-amount { font-family:'Playfair Display',serif; font-size:22px; font-weight:700; color:#C9963E; }
        .app-card-body { display:grid; grid-template-columns:repeat(3,1fr); gap:12px; margin-bottom:14px; background:var(--off-white); padding:12px; }
        .app-meta-item { font-size:12px; }
        .app-meta-label { color:var(--text-muted); margin-bottom:2px; }
        .app-meta-val { color:var(--navy); font-weight:600; }
        .app-actions { display:flex; gap:10px; flex-wrap:wrap; }
        .btn-approve { background:linear-gradient(135deg,#16a34a,#22c55e); color:white; padding:8px 18px; border-radius:4px; font-size:13px; font-weight:700; border:none; cursor:pointer; transition:all 0.2s; }
        .btn-approve:hover { transform:translateY(-1px); box-shadow:0 4px 12px rgba(34,197,94,0.3); }
        .btn-reject { background:rgba(239,68,68,0.1); color:#dc2626; padding:8px 18px; border-radius:4px; font-size:13px; font-weight:700; border:1px solid rgba(239,68,68,0.2); cursor:pointer; transition:all 0.2s; }
        .btn-reject:hover { background:rgba(239,68,68,0.15); }
        .btn-disburse { background:linear-gradient(135deg,#C9963E,#E8B96A); color:#0B1F3A; padding:8px 18px; border-radius:4px; font-size:13px; font-weight:700; border:none; cursor:pointer; transition:all 0.2s; }
        .btn-disburse:hover { transform:translateY(-1px); box-shadow:0 4px 12px rgba(201,150,62,0.3); }
        .member-row { display:flex; align-items:center; gap:14px; padding:14px 0; border-bottom:1px solid var(--border-light); flex-wrap:wrap; }
        .member-row:last-child { border-bottom:none; }
        .member-avatar { width:38px; height:38px; border-radius:50%; background:linear-gradient(135deg,#0B1F3A,#1a3a5c); display:flex; align-items:center; justify-content:center; font-family:'Playfair Display',serif; font-size:14px; font-weight:700; color:#E8B96A; flex-shrink:0; }
        .filter-tabs { display:flex; gap:8px; flex-wrap:wrap; }
        .filter-tab { padding:6px 14px; border-radius:50px; font-size:12px; font-weight:600; cursor:pointer; border:1px solid var(--border-light); background:white; color:var(--text-muted); transition:all 0.2s; }
        .filter-tab.active { background:#0B1F3A; color:white; border-color:#0B1F3A; }
        .search-input { padding:8px 14px; border:1px solid var(--border-light); border-radius:4px; font-size:13px; font-family:'DM Sans',sans-serif; outline:none; min-width:220px; }
        .search-input:focus { border-color:#C9963E; }
        .toast { position:fixed; bottom:24px; right:24px; background:#0B1F3A; color:white; padding:14px 20px; border-radius:4px; font-size:14px; font-weight:500; z-index:2000; box-shadow:0 8px 32px rgba(0,0,0,0.3); border-left:3px solid #C9963E; animation:fadeUp 0.3s ease; }
        .empty-state { text-align:center; padding:48px 20px; color:var(--text-muted); }
        .spinner-sm { width:14px; height:14px; border:2px solid rgba(255,255,255,0.3); border-top-color:white; border-radius:50%; animation:spin 0.6s linear infinite; display:inline-block; margin-right:6px; vertical-align:middle; }
        @media(max-width:1100px){ .stats-row{grid-template-columns:repeat(3,1fr);} .app-card-body{grid-template-columns:1fr 1fr;} }
        @media(max-width:800px){ .admin-sidebar{display:none;} .admin-content{padding:14px;} .stats-row{grid-template-columns:repeat(2,1fr);} }
      `}</style>

      {/* TOAST */}
      {toast && <div className="toast">{toast}</div>}

      {/* MODAL */}
      {modal && (
        <ConfirmModal
          title={modal.title}
          message={modal.message}
          confirmLabel={modal.confirmLabel}
          danger={modal.danger}
          onCancel={() => { setModal(null); setRejectReason(""); }}
          onConfirm={modal.onConfirm}
        />
      )}
      {modal?.hasInput && (
        <div style={{ position:"fixed",bottom:"50%",left:"50%",transform:"translate(-50%,200px)",zIndex:1001,width:"100%",maxWidth:440,padding:"0 20px" }}>
          <textarea
            className="form-textarea"
            placeholder="Reason for rejection (optional)..."
            value={rejectReason}
            onChange={e => setRejectReason(e.target.value)}
            rows={3}
            style={{ background:"white",marginTop:-8 }}
          />
        </div>
      )}

      <div className="admin-wrap">
        {/* SIDEBAR */}
        <aside className="admin-sidebar">
          <div style={{ padding:"20px 18px", borderBottom:"1px solid rgba(255,255,255,0.06)" }}>
            <div style={{ display:"flex",alignItems:"center",gap:10 }}>
              <div style={{ width:34,height:34,borderRadius:"50%",background:"linear-gradient(135deg,#C9963E,#E8B96A)",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'Playfair Display',serif",fontWeight:900,color:"#0B1F3A",fontSize:14,flexShrink:0 }}>RF</div>
              <div style={{ fontFamily:"'Playfair Display',serif",color:"white",fontSize:12,fontWeight:700,lineHeight:1.3 }}>Revolutionary<br/>Foundation<br/><span style={{fontSize:9,color:"#ef4444",letterSpacing:1,textTransform:"uppercase",fontFamily:"'DM Sans',sans-serif",fontWeight:600}}>Admin Panel</span></div>
            </div>
          </div>
          <nav style={{ padding:"12px 0",flex:1 }}>
            <div style={{ fontSize:9,color:"rgba(255,255,255,0.25)",letterSpacing:2,textTransform:"uppercase",padding:"10px 18px 5px" }}>Management</div>
            {NAV_ITEMS.map(item => (
              <div key={item.id} className={`admin-nav-item${active===item.id?" active":""}`} onClick={() => setActive(item.id)}>
                <span style={{ fontSize:15,width:18,textAlign:"center" }}>{item.icon}</span>
                {item.label}
                {item.id==="applications" && applications.filter(a=>a.status==="Under Review").length > 0 && (
                  <span style={{ marginLeft:"auto",background:"#ef4444",color:"white",fontSize:10,fontWeight:700,borderRadius:"50%",width:17,height:17,display:"flex",alignItems:"center",justifyContent:"center" }}>
                    {applications.filter(a=>a.status==="Under Review").length}
                  </span>
                )}
              </div>
            ))}
          </nav>
          <div style={{ padding:"14px 18px",borderTop:"1px solid rgba(255,255,255,0.06)" }}>
            <div style={{ fontSize:12,color:"rgba(255,255,255,0.4)",marginBottom:10 }}>Logged in as Admin</div>
            <div style={{ fontSize:13,color:"white",fontWeight:600,marginBottom:10 }}>{currentUser?.email}</div>
            <Link to="/dashboard" style={{ display:"block",fontSize:12,color:"rgba(255,255,255,0.3)",marginBottom:6 }}>← Member Dashboard</Link>
            <button onClick={handleLogout} style={{ fontSize:12,color:"rgba(255,255,255,0.3)",background:"none",border:"none",cursor:"pointer",padding:0 }}>Sign Out</button>
          </div>
        </aside>

        {/* MAIN */}
        <main className="admin-main">
          <div className="admin-topbar">
            <div style={{ fontFamily:"'Playfair Display',serif",fontSize:19,fontWeight:700,color:"var(--navy)" }}>
              {NAV_ITEMS.find(n=>n.id===active)?.label || "Admin"}
            </div>
            <div style={{ display:"flex",alignItems:"center",gap:12 }}>
              <input className="search-input" placeholder="🔍 Search..." value={search} onChange={e => setSearch(e.target.value)} />
              <button className="btn-primary" style={{ fontSize:13,padding:"8px 16px" }} onClick={loadData}>↻ Refresh</button>
            </div>
          </div>

          <div className="admin-content">

            {/* ── OVERVIEW ── */}
            {active==="overview" && stats && (
              <>
                <div className="stats-row">
                  {[
                    [stats.totalMembers,      "Total Members",      "var(--navy)",  "👥"],
                    [stats.paidMembers,        "Paid Members",       "#16a34a",      "✅"],
                    [stats.unpaidMembers,      "Unpaid Members",     "#dc2626",      "⏳"],
                    [stats.totalApplications,  "Applications",       "var(--navy)",  "📋"],
                    [stats.underReview,        "Pending Review",     "#2563eb",      "🔄"],
                  ].map(([val,label,color,icon]) => (
                    <div className="stat-box" key={label}>
                      <div style={{ fontSize:24,marginBottom:6 }}>{icon}</div>
                      <div className="stat-box-val" style={{ color }}>{val}</div>
                      <div className="stat-box-label">{label}</div>
                    </div>
                  ))}
                </div>
                <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:18 }}>
                  <div style={{ background:"linear-gradient(135deg,#071e0f,#0d3324)",padding:"22px",borderRadius:2 }}>
                    <div style={{ fontSize:10,color:"rgba(255,255,255,0.4)",letterSpacing:2,textTransform:"uppercase",marginBottom:8 }}>Total Funds Disbursed</div>
                    <div style={{ fontFamily:"'Playfair Display',serif",fontSize:40,fontWeight:900,color:"#E8B96A",lineHeight:1 }}>₦{stats.totalDisbursed.toLocaleString()}</div>
                    <div style={{ fontSize:12,color:"rgba(255,255,255,0.4)",marginTop:8 }}>Across {stats.disbursedCount} approved grant(s)</div>
                  </div>
                  <div style={{ background:"linear-gradient(135deg,#0B1F3A,#1a3a5c)",padding:"22px",borderRadius:2 }}>
                    <div style={{ fontSize:10,color:"rgba(255,255,255,0.4)",letterSpacing:2,textTransform:"uppercase",marginBottom:8 }}>Membership Revenue</div>
                    <div style={{ fontFamily:"'Playfair Display',serif",fontSize:40,fontWeight:900,color:"#E8B96A",lineHeight:1 }}>₦{stats.membershipRevenue.toLocaleString()}</div>
                    <div style={{ fontSize:12,color:"rgba(255,255,255,0.4)",marginTop:8 }}>{stats.paidMembers} paid members × ₦250</div>
                  </div>
                </div>

                {/* RECENT PENDING */}
                <div className="admin-card">
                  <div className="admin-card-title">
                    🔄 Pending Applications
                    <button className="filter-tab active" onClick={() => setActive("applications")}>View All →</button>
                  </div>
                  {applications.filter(a=>a.status==="Under Review").slice(0,3).map(a => (
                    <div key={a.id} style={{ display:"flex",justifyContent:"space-between",alignItems:"center",padding:"12px 0",borderBottom:"1px solid var(--border-light)",flexWrap:"wrap",gap:10 }}>
                      <div>
                        <div style={{ fontSize:14,fontWeight:600,color:"var(--navy)" }}>{a.projectTitle}</div>
                        <div style={{ fontSize:12,color:"var(--text-muted)",marginTop:2 }}>{a.orgName} · {a.state}</div>
                      </div>
                      <div style={{ display:"flex",alignItems:"center",gap:10 }}>
                        <span style={{ fontFamily:"'Playfair Display',serif",fontSize:16,fontWeight:700,color:"#C9963E" }}>₦{Number(a.fundingAmount).toLocaleString()}</span>
                        <button className="btn-approve" style={{ padding:"6px 14px",fontSize:12 }} onClick={() => handleApprove(a)}>Approve</button>
                        <button className="btn-reject" style={{ padding:"6px 14px",fontSize:12 }} onClick={() => handleReject(a)}>Reject</button>
                      </div>
                    </div>
                  ))}
                  {applications.filter(a=>a.status==="Under Review").length===0 && (
                    <div className="empty-state">✅ No pending applications</div>
                  )}
                </div>

                {/* UNPAID MEMBERS */}
                <div className="admin-card">
                  <div className="admin-card-title">
                    🪪 Unconfirmed Membership Payments
                    <button className="filter-tab active" onClick={() => setActive("members")}>View All →</button>
                  </div>
                  {members.filter(m=>!m.hasPaidMembership).slice(0,5).map(m => (
                    <div className="member-row" key={m.id}>
                      <div className="member-avatar">{m.name?.split(" ").map(n=>n[0]).join("").slice(0,2)||"?"}</div>
                      <div style={{ flex:1 }}>
                        <div style={{ fontSize:14,fontWeight:600,color:"var(--navy)" }}>{m.name}</div>
                        <div style={{ fontSize:12,color:"var(--text-muted)" }}>{m.email} · {m.state}</div>
                      </div>
                      <div style={{ display:"flex",alignItems:"center",gap:10 }}>
                        <span className="badge badge-red">✗ Unpaid</span>
                        <button className="btn-disburse" style={{ fontSize:12,padding:"6px 14px" }} onClick={() => handleConfirmPayment(m)}>
                          {actionLoading===m.id ? <><span className="spinner-sm"/>...</> : "✓ Confirm ₦250"}
                        </button>
                      </div>
                    </div>
                  ))}
                  {members.filter(m=>!m.hasPaidMembership).length===0 && (
                    <div className="empty-state">✅ All members have paid</div>
                  )}
                </div>
              </>
            )}

            {/* ── APPLICATIONS ── */}
            {active==="applications" && (
              <>
                <div style={{ display:"flex",gap:10,marginBottom:18,flexWrap:"wrap",alignItems:"center",justifyContent:"space-between" }}>
                  <div className="filter-tabs">
                    {[["all","All"],["under-review","Pending"],["approved","Approved"],["disbursed","Disbursed"],["rejected","Rejected"]].map(([val,label]) => (
                      <button key={val} className={`filter-tab${filter===val?" active":""}`} onClick={() => setFilter(val)}>{label}
                        {val==="under-review" && <span style={{ marginLeft:5,background:"#ef4444",color:"white",fontSize:10,borderRadius:"50%",width:16,height:16,display:"inline-flex",alignItems:"center",justifyContent:"center" }}>
                          {applications.filter(a=>a.status==="Under Review").length}
                        </span>}
                      </button>
                    ))}
                  </div>
                  <div style={{ fontSize:13,color:"var(--text-muted)" }}>{filteredApps.length} application(s)</div>
                </div>

                {filteredApps.length===0 ? (
                  <div className="empty-state"><div style={{fontSize:36,marginBottom:12}}>📋</div>No applications found.</div>
                ) : filteredApps.map(a => (
                  <div className="app-card" key={a.id}>
                    <div className="app-card-header">
                      <div>
                        <div className="app-card-title">{a.projectTitle}</div>
                        <div className="app-card-meta">{a.orgName} · {a.state} · Submitted {a.submittedAt?.toDate?.().toLocaleDateString()}</div>
                      </div>
                      <div style={{ textAlign:"right" }}>
                        <div className="app-card-amount">₦{Number(a.fundingAmount).toLocaleString()}</div>
                        <div style={{ marginTop:4 }}><StatusBadge status={a.status}/></div>
                      </div>
                    </div>

                    <div className="app-card-body">
                      {[["Category",a.category],["Grant Type",a.grantType?.split("(")[0]],["Timeline",a.timeline],["Beneficiaries",a.beneficiaries],["Team Size",a.teamSize||"—"],["LGA",a.lga||"—"]].map(([l,v])=>(
                        <div className="app-meta-item" key={l}>
                          <div className="app-meta-label">{l}</div>
                          <div className="app-meta-val">{v||"—"}</div>
                        </div>
                      ))}
                    </div>

                    {a.projectDesc && (
                      <div style={{ fontSize:13,color:"var(--text-muted)",lineHeight:1.7,marginBottom:14,padding:"10px",background:"var(--off-white)" }}>
                        <strong style={{ color:"var(--navy)" }}>Description:</strong> {a.projectDesc}
                      </div>
                    )}
                    {a.impact && (
                      <div style={{ fontSize:13,color:"var(--text-muted)",lineHeight:1.7,marginBottom:14 }}>
                        <strong style={{ color:"var(--navy)" }}>Expected Impact:</strong> {a.impact}
                      </div>
                    )}

                    <div className="app-actions">
                      {a.status==="Under Review" && (
                        <>
                          <button className="btn-approve" onClick={() => handleApprove(a)} disabled={actionLoading===a.id}>
                            {actionLoading===a.id ? <><span className="spinner-sm"/>Processing...</> : "✓ Approve"}
                          </button>
                          <button className="btn-reject" onClick={() => handleReject(a)} disabled={actionLoading===a.id}>✗ Reject</button>
                        </>
                      )}
                      {a.status==="Approved" && (
                        <button className="btn-disburse" onClick={() => handleDisburse(a)} disabled={actionLoading===a.id}>
                          {actionLoading===a.id ? <><span className="spinner-sm"/>Processing...</> : "💸 Mark as Disbursed"}
                        </button>
                      )}
                      {a.status==="Disbursed" && (
                        <span style={{ fontSize:13,color:"#16a34a",fontWeight:600 }}>✅ Funds disbursed on {a.disbursedAt?.toDate?.().toLocaleDateString()}</span>
                      )}
                      {a.status==="Rejected" && (
                        <span style={{ fontSize:13,color:"#dc2626",fontWeight:600 }}>✗ Rejected{a.adminNotes ? ` — ${a.adminNotes}` : ""}</span>
                      )}
                    </div>
                  </div>
                ))}
              </>
            )}

            {/* ── MEMBERS ── */}
            {active==="members" && (
              <>
                <div style={{ display:"flex",gap:10,marginBottom:18,flexWrap:"wrap",alignItems:"center",justifyContent:"space-between" }}>
                  <div className="filter-tabs">
                    {[["all","All Members"],["paid","Paid"],["unpaid","Unpaid"]].map(([val,label]) => (
                      <button key={val} className={`filter-tab${filter===val?" active":""}`} onClick={() => setFilter(val)}>{label}</button>
                    ))}
                  </div>
                  <div style={{ fontSize:13,color:"var(--text-muted)" }}>{filteredMembers.length} member(s)</div>
                </div>
                <div className="admin-card">
                  {filteredMembers
                    .filter(m => filter==="all" || (filter==="paid" && m.hasPaidMembership) || (filter==="unpaid" && !m.hasPaidMembership))
                    .map(m => (
                    <div className="member-row" key={m.id}>
                      <div className="member-avatar">{m.name?.split(" ").map(n=>n[0]).join("").slice(0,2)||"?"}</div>
                      <div style={{ flex:1,minWidth:0 }}>
                        <div style={{ fontSize:14,fontWeight:600,color:"var(--navy)" }}>{m.name}</div>
                        <div style={{ fontSize:12,color:"var(--text-muted)",marginTop:1 }}>{m.email} · {m.org||"—"} · {m.state||"—"}</div>
                        <div style={{ fontSize:11,color:"var(--text-muted)",marginTop:1 }}>ID: {m.memberId||"—"} · Joined: {m.createdAt?.toDate?.().toLocaleDateString()||"—"}</div>
                      </div>
                      <div style={{ display:"flex",alignItems:"center",gap:10,flexShrink:0 }}>
                        <span className={`badge ${m.hasPaidMembership?"badge-green":"badge-red"}`}>
                          {m.hasPaidMembership?"✓ Paid":"✗ Unpaid"}
                        </span>
                        {!m.hasPaidMembership && (
                          <button className="btn-disburse" style={{ fontSize:12,padding:"6px 12px" }} onClick={() => handleConfirmPayment(m)} disabled={actionLoading===m.id}>
                            {actionLoading===m.id ? "..." : "Confirm ₦250"}
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                  {filteredMembers.length===0 && <div className="empty-state">No members found.</div>}
                </div>
              </>
            )}

            {/* ── FUND MANAGEMENT ── */}
            {active==="funds" && (
              <>
                <div style={{ display:"grid",gridTemplateColumns:"repeat(3, 1fr)",gap:14,marginBottom:20 }}>
                  {[
                    [`₦${stats?.totalDisbursed?.toLocaleString()||0}`,"Total Disbursed","#16a34a"],
                    [`₦${(applications.filter(a=>a.status==="Approved").reduce((s,a)=>s+Number(a.fundingAmount||0),0) + applications.filter(a=>a.status==="Under Review").reduce((s,a)=>s+Number(a.fundingAmount||0),0)).toLocaleString()}`,"Awaiting Release","#57534e"],
                    [`₦${stats?.membershipRevenue?.toLocaleString()||0}`,"Membership Revenue","#C9963E"],
                  ].map(([val,label,color])=>(
                    <div className="stat-box" key={label}>
                      <div className="stat-box-val" style={{color}}>{val}</div>
                      <div className="stat-box-label" style={{ fontSize:11 }}>{label}</div>
                    </div>
                  ))}
                </div>
                <div className="admin-card">
                  <div className="admin-card-title">Pending Review Applications</div>
                  {applications.filter(a=>a.status==="Under Review").length===0 ? (
                    <div className="empty-state">No applications pending review.</div>
                  ) : applications.filter(a=>a.status==="Under Review").map(a=>(
                    <div key={a.id} style={{ display:"flex",justifyContent:"space-between",alignItems:"center",padding:"14px 0",borderBottom:"1px solid var(--border-light)",flexWrap:"wrap",gap:10 }}>
                      <div>
                        <div style={{ fontSize:14,fontWeight:600,color:"var(--navy)" }}>{a.projectTitle}</div>
                        <div style={{ fontSize:12,color:"var(--text-muted)",marginTop:2 }}>{a.orgName} · Submitted {a.submittedAt?.toDate?.().toLocaleDateString()}</div>
                      </div>
                      <div style={{ display:"flex",alignItems:"center",gap:12 }}>
                        <span style={{ fontFamily:"'Playfair Display',serif",fontSize:18,fontWeight:700,color:"#C9963E" }}>₦{Number(a.fundingAmount).toLocaleString()}</span>
                        <button className="btn-approve" onClick={() => handleApprove(a)} disabled={actionLoading===a.id}>
                          {actionLoading===a.id ? "..." : "✓ Approve"}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="admin-card">
                  <div className="admin-card-title">Approved — Awaiting Disbursement</div>
                  {applications.filter(a=>a.status==="Approved").length===0 ? (
                    <div className="empty-state">No approved applications pending disbursement.</div>
                  ) : applications.filter(a=>a.status==="Approved").map(a=>(
                    <div key={a.id} style={{ display:"flex",justifyContent:"space-between",alignItems:"center",padding:"14px 0",borderBottom:"1px solid var(--border-light)",flexWrap:"wrap",gap:10 }}>
                      <div>
                        <div style={{ fontSize:14,fontWeight:600,color:"var(--navy)" }}>{a.projectTitle}</div>
                        <div style={{ fontSize:12,color:"var(--text-muted)",marginTop:2 }}>{a.orgName} · Approved {a.updatedAt?.toDate?.().toLocaleDateString()}</div>
                      </div>
                      <div style={{ display:"flex",alignItems:"center",gap:12 }}>
                        <span style={{ fontFamily:"'Playfair Display',serif",fontSize:18,fontWeight:700,color:"#C9963E" }}>₦{Number(a.fundingAmount).toLocaleString()}</span>
                        <button className="btn-disburse" onClick={() => handleDisburse(a)} disabled={actionLoading===a.id}>
                          {actionLoading===a.id ? "Processing..." : "💸 Disburse Now"}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="admin-card">
                  <div className="admin-card-title">Disbursement History</div>
                  {applications.filter(a=>a.status==="Disbursed").length===0 ? (
                    <div className="empty-state">No disbursements yet.</div>
                  ) : applications.filter(a=>a.status==="Disbursed").map(a=>(
                    <div key={a.id} style={{ display:"flex",justifyContent:"space-between",alignItems:"center",padding:"12px 0",borderBottom:"1px solid var(--border-light)",flexWrap:"wrap",gap:10 }}>
                      <div>
                        <div style={{ fontSize:14,fontWeight:600,color:"var(--navy)" }}>{a.projectTitle}</div>
                        <div style={{ fontSize:12,color:"var(--text-muted)",marginTop:2 }}>{a.orgName} · {a.disbursedAt?.toDate?.().toLocaleDateString()}</div>
                      </div>
                      <div style={{ display:"flex",alignItems:"center",gap:10 }}>
                        <span style={{ fontFamily:"'Playfair Display',serif",fontSize:16,fontWeight:700,color:"#C9963E" }}>₦{Number(a.fundingAmount).toLocaleString()}</span>
                        <StatusBadge status="Disbursed"/>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* ── ANALYTICS ── */}
            {active==="analytics" && stats && (
              <>
                <div className="stats-row">
                  {[
                    [stats.totalMembers,     "Total Members",   "var(--navy)"],
                    [stats.paidMembers,       "Paid Members",    "#16a34a"],
                    [stats.approved,          "Approved Grants", "#C9963E"],
                    [stats.rejected,          "Rejected",        "#dc2626"],
                    [stats.disbursedCount,    "Disbursed",       "#7c3aed"],
                  ].map(([val,label,color])=>(
                    <div className="stat-box" key={label}>
                      <div className="stat-box-val" style={{color}}>{val}</div>
                      <div className="stat-box-label">{label}</div>
                    </div>
                  ))}
                </div>
                <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:16 }}>
                  <div className="admin-card">
                    <div className="admin-card-title">Application Breakdown</div>
                    {[
                      ["Under Review", applications.filter(a=>a.status==="Under Review").length, "#2563eb"],
                      ["Approved",     applications.filter(a=>a.status==="Approved").length,     "#16a34a"],
                      ["Disbursed",    applications.filter(a=>a.status==="Disbursed").length,    "#C9963E"],
                      ["Rejected",     applications.filter(a=>a.status==="Rejected").length,     "#dc2626"],
                    ].map(([label,count,color])=>(
                      <div key={label} style={{ marginBottom:14 }}>
                        <div style={{ display:"flex",justifyContent:"space-between",fontSize:13,marginBottom:5 }}>
                          <span style={{ color:"var(--navy)",fontWeight:500 }}>{label}</span>
                          <span style={{ color,fontWeight:700 }}>{count}</span>
                        </div>
                        <div style={{ height:6,background:"var(--off-white)",borderRadius:3 }}>
                          <div style={{ height:"100%",width:`${stats.totalApplications>0?(count/stats.totalApplications*100):0}%`,background:color,borderRadius:3,transition:"width 1s" }}/>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="admin-card">
                    <div className="admin-card-title">Financial Summary</div>
                    {[
                      ["Membership Revenue", `₦${stats.membershipRevenue.toLocaleString()}`],
                      ["Total Disbursed",    `₦${stats.totalDisbursed.toLocaleString()}`],
                      ["Pending Disbursement", `₦${applications.filter(a=>a.status==="Approved").reduce((s,a)=>s+Number(a.fundingAmount||0),0).toLocaleString()}`],
                      ["Total Applications Received", stats.totalApplications],
                      ["Approval Rate", stats.totalApplications>0?`${Math.round((stats.approved+stats.disbursedCount)/stats.totalApplications*100)}%`:"0%"],
                    ].map(([l,v])=>(
                      <div key={l} style={{ display:"flex",justifyContent:"space-between",padding:"10px 0",borderBottom:"1px solid var(--border-light)" }}>
                        <span style={{ fontSize:13,color:"var(--text-muted)" }}>{l}</span>
                        <span style={{ fontSize:13,fontWeight:700,color:"var(--navy)" }}>{v}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* ── NOTIFICATIONS ── */}
            {active==="notifications" && (
              <div className="admin-card">
                <div className="admin-card-title">System Activity</div>
                {applications.slice(0,10).map(a=>(
                  <div key={a.id} style={{ display:"flex",gap:12,padding:"12px 0",borderBottom:"1px solid var(--border-light)",alignItems:"flex-start" }}>
                    <div style={{ fontSize:18,flexShrink:0 }}>
                      {a.status==="Approved"?"✅":a.status==="Rejected"?"❌":a.status==="Disbursed"?"💸":"🔄"}
                    </div>
                    <div>
                      <div style={{ fontSize:13,fontWeight:600,color:"var(--navy)" }}>{a.projectTitle}</div>
                      <div style={{ fontSize:12,color:"var(--text-muted)",marginTop:2 }}>{a.orgName} · Status: <strong>{a.status}</strong> · ₦{Number(a.fundingAmount).toLocaleString()}</div>
                      <div style={{ fontSize:11,color:"var(--text-muted)",marginTop:2 }}>{a.submittedAt?.toDate?.().toLocaleDateString()}</div>
                    </div>
                  </div>
                ))}
                {applications.length===0&&<div className="empty-state">No activity yet.</div>}
              </div>
            )}

          </div>
        </main>
      </div>
    </>
  );
}
